import { useEffect, useState } from 'react'
import { Calendar } from 'primereact/calendar'
import { InputTextarea } from 'primereact/inputtextarea'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import { useToast } from '../../../components/ui/ToastProvider'
import { todayStart, toISODate } from '../../../lib/datetime'
import MainTaskDetailView from '../../actions/components/MainTaskDetailView'
import { useReopenTask } from '../hooks/useMeetingManagement'
import { NOTE_MAX_LENGTH, type MeetingManagementTask } from '../types'

interface Props {
  visible: boolean
  /** İşlem başlangıcında seçilmiş ana görevler — pop-up açılırken **bir kez** alınır. */
  tasks: MeetingManagementTask[]
  onHide: () => void
}

/**
 * <b>Yeniden Açma</b> pop-up'ı (105) — iki bölme.
 *
 * <p><b>Sol:</b> işleme alınacak görevler. <b>Sağ:</b> seçili görevin salt-okuma detayı; yapı
 * "Ana Görevi Gör" ekranıyla <b>aynıdır</b> çünkü aynı bileşendir ({@link MainTaskDetailView}) —
 * spec FR-036 bunu istiyor ve ortak bileşen iki yüzeyin ayrışmasını yapısal olarak imkânsız kılıyor.
 *
 * <p><b>Görevler tek tek işlenir</b> (FR-044): her görev için <b>ayrı</b> hedef teslim tarihi ve
 * <b>ayrı</b> not girilir, uç da görev başına çağrılır. Toplu bir "hepsine aynı tarih" yolu
 * bilinçle yoktur — analiz açıkça tek tek işlemeyi tarif ediyor.
 *
 * <p>Başarılı işlemden sonra görev soldan düşer, <b>sıradaki otomatik seçilir</b> ve form alanları
 * <b>boşalır</b>: bir görevin tarihi/notu ötekine taşınmaz. Başarısız işlemde görev soldan
 * <b>düşmez</b> ve seçili kalır; kullanıcı düzeltip yeniden dener.
 *
 * <p><b>Not alanında `maxLength` KULLANILMAZ</b> (FR-047a): sınırın üzerine yazmayı engelleyen bir
 * kısıt, fazlasını sessizce keser ve FR-045'teki uyarı metni hiçbir zaman görünmezdi. Alan yazmaya
 * izin verir, sayaç kalanı gösterir, gönderim engellenir ve uyarı çıkar.
 */
export default function ReopenTasksDialog({ visible, tasks, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const reopen = useReopenTask()

  const [queue, setQueue] = useState<MeetingManagementTask[]>([])
  const [currentId, setCurrentId] = useState<number | null>(null)
  const [deadline, setDeadline] = useState<Date | null>(null)
  const [note, setNote] = useState('')

  // Kuyruk pop-up AÇILIRKEN bir kez alınır: sonradan listenin tazelenmesi (arka planda) işleme
  // alınmış kuyruğu değiştirmemeli — kullanıcı gördüğü sırayla çalışır.
  useEffect(() => {
    if (!visible) return
    setQueue(tasks)
    setCurrentId(tasks.length > 0 ? tasks[0].id : null)
    setDeadline(null)
    setNote('')
    // `tasks` referansı her render'da değişebilir; kuyruk YALNIZ görünürlük değişiminde kurulur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const current = queue.find((q) => q.id === currentId) ?? null
  const noteLength = note.trim().length
  const noteTooLong = noteLength > NOTE_MAX_LENGTH

  /** Sıradaki göreve geç ve formu sıfırla — bir görevin değerleri ötekine taşınmaz (FR-044). */
  const focusTask = (id: number | null) => {
    setCurrentId(id)
    setDeadline(null)
    setNote('')
  }

  const submit = () => {
    if (!current || reopen.isPending) return

    // Doğrulama başarısızsa HİÇBİR sunucu çağrısı yapılmaz (FR-046).
    if (deadline == null) {
      toast.error(t('meetingManagement.reopen.deadlineRequired'))
      return
    }
    if (deadline < todayStart()) {
      toast.error(t('meetingManagement.reopen.deadlinePast'))
      return
    }
    // NOT: bu dal şu an UI'dan ERİŞİLEMEZ — `submitDisabled` `noteTooLong`u da içeriyor ve hem
    // görünür butonu hem `FormDialog`un Enter-submit yolunu kapatıyor. Bilinçle duruyor (FR-046);
    // kullanıcıya asıl bildirim alanın `aria-live` hata bölgesinden gider. Üstteki iki tarih dalı
    // ise ERİŞİLEBİLİR kalır: `submitDisabled` yalnız `deadline == null`a bakar, geçmiş tarihe değil.
    if (noteTooLong) {
      toast.error(t('meetingManagement.reopen.noteTooLong', { max: NOTE_MAX_LENGTH }))
      return
    }

    const trimmed = note.trim()
    reopen.mutate(
      {
        id: current.id,
        body: {
          newDeadline: toISODate(deadline),
          note: trimmed === '' ? null : trimmed,
          modDate: current.modDate,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('meetingManagement.reopen.success'))
          const remaining = queue.filter((q) => q.id !== current.id)
          setQueue(remaining)
          if (remaining.length === 0) {
            onHide()
            return
          }
          focusTask(remaining[0].id)
        },
        // Hata → merkezî bildirim. Görev kuyrukta KALIR ve seçili kalır (FR-041): kullanıcı
        // düzeltip yeniden deneyebilsin.
      },
    )
  }

  return (
    <FormDialog
      visible={visible}
      header={t('meetingManagement.reopen.title')}
      submitLabel={t('meetingManagement.action.reopen')}
      submitting={reopen.isPending}
      submitDisabled={current == null || deadline == null || noteTooLong}
      onSubmit={submit}
      onHide={onHide}
      // İki bölme yan yana duruyor ve sağdaki salt-okuma detay Görev Detay ekranının tamamını
      // taşıyor; dar bir pop-up onu okunmaz hâle getiriyordu. Paylaşılan kabuk zaten
      // `maxWidth: 92vw` uyguluyor, dolayısıyla bu değer küçük ekranda görüntü alanını taşırmaz.
      width="84rem"
    >
      <div className="mm-reopen-body">
        {/* SOL — işleme alınacak görevler. Kullanıcı sıraya uymak zorunda değil (FR-035). */}
        <nav className="mm-reopen-queue" aria-label={t('meetingManagement.reopen.queueLabel')}>
          {queue.map((task) => (
            <button
              key={task.id}
              type="button"
              className="mm-reopen-queue-item"
              aria-current={task.id === currentId}
              onClick={() => focusTask(task.id)}
            >
              {task.name}
            </button>
          ))}
        </nav>

        {/* SAĞ — üstte o görevin alanları, altında salt-okuma detayı. */}
        <div className="mm-reopen-detail">
          <FormGrid>
            <FormField
              label={t('meetingManagement.reopen.newDeadline')}
              htmlFor="mm-reopen-deadline"
              required
              full
            >
              <Calendar
                inputId="mm-reopen-deadline"
                value={deadline}
                onChange={(e) => setDeadline((e.value as Date | null) ?? null)}
                dateFormat="dd.mm.yy"
                // Bugün SEÇİLEBİLİR, dünü ve öncesi seçilemez (FR-038).
                minDate={todayStart()}
                showIcon
                readOnlyInput
                // Takvim paneli dialog'un DIŞINA çizilir; içeride kalsaydı gövde kaydırma çubuğu
                // açar ve takvimin yarısı kırpılırdı.
                appendTo={document.body}
              />
            </FormField>

            <FormField
              label={t('meetingManagement.reopen.note')}
              htmlFor="mm-reopen-note"
              // Hata metni paylaşılan alandan gelir; kutu ELLE kurulmaz (Reuse Mandate). Gerekçe ve
              // canlı-bölge kararı `FormField` içinde yazılı.
              error={
                noteTooLong
                  ? t('meetingManagement.reopen.noteTooLong', { max: NOTE_MAX_LENGTH })
                  : undefined
              }
              full
            >
              <InputTextarea
                id="mm-reopen-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                autoResize
                // Gerekçe: `CompletionNoteDialog`'daki aynı not (THY inceleme bulgusu, 16.09.2026).
                // Burada Kaydet `submitDisabled` ile pasifleştiği için `submit()` içindeki uyarı da
                // sınıra takılan kullanıcıya hiç ulaşmıyordu.
                aria-invalid={noteTooLong}
                aria-describedby="mm-reopen-note-counter mm-reopen-note-err"
              />
              {/* Sayaç bilinçle canlı DEĞİL — gerekçe `CompletionNoteDialog`'da yazılı. */}
              <small
                id="mm-reopen-note-counter"
                className={`mm-note-counter${noteTooLong ? ' mm-over' : ''}`}
              >
                {noteLength} / {NOTE_MAX_LENGTH}
              </small>
            </FormField>
          </FormGrid>

          {current != null && <MainTaskDetailView actionId={current.id} active={visible} />}
        </div>
      </div>
    </FormDialog>
  )
}
